import React from "react";

import { Copy, Check } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface CopyableCellProps {
  value: string;
  display: React.ReactNode;
  tooltipLabel: string;
  iconSize?: number;
  iconClassName?: string;
  buttonClassName?: string;
  onCopy?: () => void | Promise<void>;
}

export const CopyableCell: React.FC<CopyableCellProps> = ({
  value,
  display,
  tooltipLabel,
  iconSize = 16,
  iconClassName = "text-muted-foreground",
  buttonClassName,
  onCopy,
}) => {
  const [copied, setCopied] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const handleCopy = async () => {
    if (onCopy) {
      await onCopy();
    } else {
      await navigator.clipboard.writeText(value);
    }
    setCopied(true);
    setOpen(true);
    setTimeout(() => setCopied(false), 1200);
  };
  return (
    <span className="flex items-center gap-1">
      {display}
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopy}
            className={cn("h-7 w-7 p-0", buttonClassName)}
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
          >
            {copied ? (
              <Check size={iconSize} className={iconClassName} />
            ) : (
              <Copy size={iconSize} className={iconClassName} />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{copied ? "Copied" : tooltipLabel}</TooltipContent>
      </Tooltip>
    </span>
  );
};
